import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { useAuth } from './AuthContext'
import { config } from '../config/env'
import { getUsableSessionAccessToken } from '../lib/sessionAccessToken'

export interface Clip {
  id: string
  user_id: string
  job_id: string
  filename: string
  title: string
  duration: number
  file_size: number
  storage_path: string
  stream_url?: string
  created_at: string
  updated_at?: string
}

export interface ClipsDataError {
  kind: 'authentication' | 'request'
  message: string
}

interface ClipsContextType {
  clips: Clip[] | null
  loading: boolean
  refreshClips: (limit?: number, offset?: number) => Promise<void>
  loadMoreClips: (limit: number) => Promise<void>
  deleteClip: (clipId: string) => Promise<boolean>
  addClip: (clip: Clip) => void
  hasMore: boolean
  totalCount: number
  error: ClipsDataError | null
}

const ClipsContext = createContext<ClipsContextType | undefined>(undefined)

export const useClips = () => {
  const context = useContext(ClipsContext)
  if (!context) {
    throw new Error('useClips must be used within a ClipsProvider')
  }
  return context
}

const authenticationError = (action: 'load' | 'delete'): ClipsDataError => ({
  kind: 'authentication',
  message:
    action === 'load'
      ? 'Your sign-in session is unavailable or expired. Sign in again to load your clips.'
      : 'Your sign-in session is unavailable or expired. Sign in again before deleting a clip.',
})

const requestError = (action: 'load' | 'delete'): ClipsDataError => ({
  kind: 'request',
  message:
    action === 'load'
      ? 'Your clips are temporarily unavailable. Retry in a moment.'
      : 'That clip could not be deleted. Retry in a moment.',
})

const responseError = (
  status: number,
  action: 'load' | 'delete'
): ClipsDataError =>
  status === 401 || status === 403
    ? authenticationError(action)
    : requestError(action)

interface ClipsFetchRequest {
  ownerId: string
  generation: number
  key: object
  promise: Promise<void>
}

export const ClipsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, session } = useAuth()
  const currentOwnerId = user?.id ?? null
  const [stateOwnerId, setStateOwnerId] = useState<string | null>(
    currentOwnerId
  )
  const [storedClips, setClips] = useState<Clip[] | null>(null)
  const [storedLoading, setLoading] = useState(false)
  const [storedHasMore, setHasMore] = useState(false)
  const [storedTotalCount, setTotalCount] = useState(0)
  const [storedError, setError] = useState<ClipsDataError | null>(null)
  const hasFetched = useRef(false)
  const fetchPromiseRef = useRef<ClipsFetchRequest | null>(null)
  const clipsLengthRef = useRef(0)
  const activeOwnerRef = useRef<string | null>(currentOwnerId)
  const ownerGenerationRef = useRef(0)

  const stateMatchesOwner = stateOwnerId === currentOwnerId
  const clips = stateMatchesOwner ? storedClips : null
  const loading = stateMatchesOwner ? storedLoading : false
  const hasMore = stateMatchesOwner ? storedHasMore : false
  const totalCount = stateMatchesOwner ? storedTotalCount : 0
  const error = stateMatchesOwner ? storedError : null

  const isActiveOwnerRequest = useCallback(
    (ownerId: string, generation: number) =>
      activeOwnerRef.current === ownerId &&
      ownerGenerationRef.current === generation,
    []
  )

  const refreshClips = useCallback(
    async (
      limit?: number,
      offset: number = 0,
      isLoadMore: boolean = false
    ) => {
      const ownerId = user?.id
      const generation = ownerGenerationRef.current
      if (!ownerId || !isActiveOwnerRequest(ownerId, generation)) return

      const accessToken = getUsableSessionAccessToken(session)
      if (!accessToken) {
        if (isActiveOwnerRequest(ownerId, generation)) {
          setError(authenticationError('load'))
          setLoading(false)
        }
        return
      }

      const activeFetch = fetchPromiseRef.current
      if (
        activeFetch?.ownerId === ownerId &&
        activeFetch.generation === generation
      ) {
        return activeFetch.promise
      }

      const requestKey = {}
      const requestRecord: ClipsFetchRequest = {
        ownerId,
        generation,
        key: requestKey,
        promise: Promise.resolve(),
      }
      fetchPromiseRef.current = requestRecord

      const fetchPromise = (async () => {
        try {
          if (isActiveOwnerRequest(ownerId, generation)) {
            if (!isLoadMore) setLoading(true)
            setError(null)
          }

          const url = new URL(config.endpoints.userClips(ownerId))
          if (limit !== undefined) {
            url.searchParams.append('limit', limit.toString())
          }
          if (offset > 0) {
            url.searchParams.append('offset', offset.toString())
          }

          const response = await fetch(url.toString(), {
            headers: {
              Accept: 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
          })

          if (!response.ok) {
            if (isActiveOwnerRequest(ownerId, generation)) {
              setError(responseError(response.status, 'load'))
            }
            return
          }

          const clipsData = await response.json()
          if (
            !clipsData ||
            !Array.isArray(clipsData.clips) ||
            typeof clipsData.total !== 'number' ||
            !Number.isInteger(clipsData.total) ||
            clipsData.total < 0 ||
            typeof clipsData.has_more !== 'boolean'
          ) {
            throw new Error('Invalid clips response')
          }

          if (!isActiveOwnerRequest(ownerId, generation)) return

          if (offset > 0) {
            setClips((previousClips) => {
              const nextClips = [
                ...(previousClips || []),
                ...(clipsData.clips as Clip[]),
              ]
              clipsLengthRef.current = nextClips.length
              return nextClips
            })
          } else {
            const nextClips = clipsData.clips as Clip[]
            setClips(nextClips)
            clipsLengthRef.current = nextClips.length
          }

          setTotalCount(clipsData.total)
          setHasMore(clipsData.has_more)
          setError(null)
        } catch {
          if (isActiveOwnerRequest(ownerId, generation)) {
            setError(requestError('load'))
          }
        } finally {
          if (
            isActiveOwnerRequest(ownerId, generation) &&
            !isLoadMore
          ) {
            setLoading(false)
          }
          if (fetchPromiseRef.current?.key === requestKey) {
            fetchPromiseRef.current = null
          }
        }
      })()

      requestRecord.promise = fetchPromise
      return fetchPromise
    },
    [isActiveOwnerRequest, session, user?.id]
  )

  const deleteClip = useCallback(
    async (clipId: string): Promise<boolean> => {
      const ownerId = user?.id
      const generation = ownerGenerationRef.current
      if (!ownerId || !isActiveOwnerRequest(ownerId, generation)) {
        return false
      }
      const accessToken = getUsableSessionAccessToken(session)
      if (!accessToken) {
        if (isActiveOwnerRequest(ownerId, generation)) {
          setError(authenticationError('delete'))
        }
        return false
      }

      try {
        setError(null)
        const response = await fetch(
          config.endpoints.deleteClip(ownerId, clipId),
          {
            method: 'DELETE',
            headers: {
              Accept: 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
          }
        )

        if (!response.ok) {
          if (isActiveOwnerRequest(ownerId, generation)) {
            setError(responseError(response.status, 'delete'))
          }
          return false
        }

        if (!isActiveOwnerRequest(ownerId, generation)) return false

        setClips((previousClips) => {
          const nextClips = previousClips
            ? previousClips.filter((clip) => clip.id !== clipId)
            : null
          clipsLengthRef.current = nextClips?.length ?? 0
          return nextClips
        })
        setTotalCount((previousTotal) => Math.max(0, previousTotal - 1))
        return true
      } catch {
        if (isActiveOwnerRequest(ownerId, generation)) {
          setError(requestError('delete'))
        }
        return false
      }
    },
    [isActiveOwnerRequest, session, user?.id]
  )

  const loadMoreClips = useCallback(
    async (limit: number) => {
      if (!user?.id || loading) return
      await refreshClips(limit, clipsLengthRef.current, true)
    },
    [loading, refreshClips, user?.id]
  )

  const addClip = useCallback(
    (clip: Clip) => {
      const ownerId = user?.id
      const generation = ownerGenerationRef.current
      if (
        !ownerId ||
        clip.user_id !== ownerId ||
        !isActiveOwnerRequest(ownerId, generation)
      ) {
        return
      }

      setClips((previousClips) => {
        const nextClips = previousClips
          ? [clip, ...previousClips]
          : [clip]
        clipsLengthRef.current = nextClips.length
        return nextClips
      })
      setTotalCount((previousTotal) => previousTotal + 1)
    },
    [isActiveOwnerRequest, user?.id]
  )

  useLayoutEffect(() => {
    activeOwnerRef.current = currentOwnerId
    ownerGenerationRef.current += 1
    fetchPromiseRef.current = null
    hasFetched.current = false
    clipsLengthRef.current = 0
    setStateOwnerId(currentOwnerId)
    setClips(null)
    setLoading(false)
    setHasMore(false)
    setTotalCount(0)
    setError(null)
  }, [currentOwnerId])

  useEffect(() => {
    const accessToken = getUsableSessionAccessToken(session)

    if (currentOwnerId) {
      if (!accessToken) {
        hasFetched.current = false
        const generation = ownerGenerationRef.current
        if (isActiveOwnerRequest(currentOwnerId, generation)) {
          setError(authenticationError('load'))
          setLoading(false)
        }
      } else if (!hasFetched.current) {
        hasFetched.current = true
        void refreshClips(5)
      }
    }
  }, [
    currentOwnerId,
    isActiveOwnerRequest,
    refreshClips,
    session,
  ])

  return (
    <ClipsContext.Provider
      value={{
        clips,
        loading,
        refreshClips,
        loadMoreClips,
        deleteClip,
        addClip,
        hasMore,
        totalCount,
        error,
      }}
    >
      {children}
    </ClipsContext.Provider>
  )
}
